import { useRef, useEffect, useState, useMemo } from 'react';
import { Users, CircleDot, Bell, Square, Circle, RectangleHorizontal } from 'lucide-react';
import { cur } from '@/lib/currency';

// Mirrors D2 FloorDesigner's logical coordinate model (kept local so the FROZEN
// D2 file is never touched). Read-only: no drag, no save, no coordinate writes.
const LOGICAL_W = 1000;
const LOGICAL_H = 600;
const NODE_PX = 92;
// Auto-arrange grid for UNPOSITIONED (0,0) tables — same shape as D2's.
const COLS = 6;
const CELL_W = (LOGICAL_W - 40) / COLS;
const ROW_H = 130;

const SHAPE_ICON = { square: Square, round: Circle, rectangle: RectangleHorizontal };

const ST = {
  available: { ring: 'border-emerald-400 bg-emerald-50 dark:bg-emerald-500/10', text: 'text-emerald-600', label: 'Available' },
  occupied: { ring: 'border-red-400 bg-red-50 dark:bg-red-500/10', text: 'text-red-600', label: 'Occupied' },
  payment_requested: { ring: 'border-amber-500 bg-amber-100 ring-2 ring-amber-300 dark:bg-amber-500/15', text: 'text-amber-700', label: 'Payment' },
  reserved: { ring: 'border-amber-400 bg-amber-50 dark:bg-amber-500/10', text: 'text-amber-600', label: 'Reserved' },
  cleaning: { ring: 'border-blue-400 bg-blue-50 dark:bg-blue-500/10', text: 'text-blue-600', label: 'Cleaning' },
};

const isPositioned = (t) => (t.position_x > 0 || t.position_y > 0);

// Partial-layout safeguard: positioned tables keep their saved coords; any table
// still at 0,0 is placed into the first grid cell NOT occupied by a positioned
// table (so a manager's saved tables are not covered). Frontend-only — never
// persisted. Returns { [id]: {x,y} } in logical units + the used logical height.
function layoutPositions(tables) {
  const pos = {};
  const occupied = new Set();
  const cellOf = (x, y) => {
    const c = Math.max(0, Math.min(COLS - 1, Math.round((x - 20) / CELL_W)));
    const r = Math.max(0, Math.round((y - 30) / ROW_H));
    return `${c},${r}`;
  };
  tables.forEach((t) => {
    if (isPositioned(t)) {
      pos[t.id] = { x: t.position_x, y: t.position_y };
      occupied.add(cellOf(t.position_x, t.position_y));
    }
  });
  const unplaced = tables.filter((t) => !isPositioned(t));
  let cursor = 0;
  unplaced.forEach((t) => {
    // advance to the next free cell
    let c = cursor % COLS;
    let r = Math.floor(cursor / COLS);
    while (occupied.has(`${c},${r}`)) {
      cursor += 1;
      c = cursor % COLS;
      r = Math.floor(cursor / COLS);
    }
    occupied.add(`${c},${r}`);
    pos[t.id] = { x: Math.round(20 + c * CELL_W), y: Math.round(30 + r * ROW_H) };
    cursor += 1;
  });
  const maxY = Object.values(pos).reduce((m, p) => Math.max(m, p.y), 0);
  const logicalH = Math.max(LOGICAL_H, maxY + ROW_H);
  return { pos, logicalH };
}

function FloorNode({ table, pos, scale, maxLeft, maxTop, onSelect }) {
  const displayStatus = table.payment_requested ? 'payment_requested' : table.status;
  const s = ST[displayStatus] || ST.available;
  const ShapeIcon = SHAPE_ICON[table.shape] || Square;
  const checksCount = table.open_checks_count || 0;
  const left = Math.max(0, Math.min(pos.x * scale, maxLeft));
  const top = Math.max(0, Math.min(pos.y * scale, maxTop));
  const rounded = table.shape === 'round' ? 'rounded-full' : 'rounded-xl';

  return (
    <button
      type="button"
      onClick={() => onSelect(table)}
      style={{ position: 'absolute', left, top, width: NODE_PX, height: NODE_PX }}
      className={`border-2 ${s.ring} ${rounded} shadow-sm flex flex-col items-center justify-center text-center transition-transform active:scale-95 hover:shadow-md`}
      data-testid={`waiter-floor-table-${table.table_number}`}
    >
      {checksCount > 1 && (
        <span
          className="absolute -top-1.5 -right-1.5 px-1.5 py-0.5 rounded-full bg-zinc-900 text-white text-[9px] font-bold"
          data-testid={`waiter-floor-checks-${table.table_number}`}
        >
          {checksCount}
        </span>
      )}
      <ShapeIcon size={14} className={`${s.text} mb-0.5`} />
      <span className="font-bold text-sm leading-none dark:text-white">{table.table_number}</span>
      <span className={`flex items-center gap-0.5 text-[10px] mt-0.5 ${s.text}`}>
        <Users size={10} />{table.customer_count || 0}/{table.capacity}
      </span>
      {table.payment_requested ? (
        <span className="flex items-center gap-0.5 text-[9px] text-amber-700 mt-0.5" data-testid={`waiter-floor-status-${table.table_number}`}>
          <Bell size={9} />{s.label}
        </span>
      ) : table.current_order ? (
        <span className="text-[9px] font-semibold text-red-600 mt-0.5">{cur()} {Number(table.current_order.total || 0).toFixed(0)}</span>
      ) : (
        <span className="flex items-center gap-0.5 text-[9px] mt-0.5 opacity-70" data-testid={`waiter-floor-status-${table.table_number}`}>
          <CircleDot size={9} />{s.label}
        </span>
      )}
    </button>
  );
}

export function WaiterFloorView({ tables, sectionName, onSelectTable }) {
  const containerRef = useRef(null);
  const [canvasW, setCanvasW] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setCanvasW(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { pos, logicalH } = useMemo(() => layoutPositions(tables), [tables]);
  const scale = canvasW ? canvasW / LOGICAL_W : 0;
  const canvasH = canvasW ? logicalH * scale : 360;
  const maxLeft = canvasW - NODE_PX;
  const maxTop = canvasH - NODE_PX;

  return (
    <div className="p-4" data-testid="waiter-floor-view">
      <div className="flex items-center gap-2 mb-2">
        <h2 className="font-semibold text-base font-outfit">{sectionName}</h2>
        <span className="text-xs text-muted-foreground">· {tables.length} tables · Floor layout</span>
      </div>
      <div
        ref={containerRef}
        className="relative w-full rounded-xl border-2 border-dashed border-muted bg-[linear-gradient(to_right,rgba(0,0,0,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(0,0,0,0.03)_1px,transparent_1px)] bg-[length:40px_40px]"
        style={{ height: canvasH }}
        data-testid="waiter-floor-canvas"
      >
        {scale > 0 && tables.map((t) => (
          <FloorNode
            key={t.id}
            table={t}
            pos={pos[t.id] || { x: 0, y: 0 }}
            scale={scale}
            maxLeft={maxLeft}
            maxTop={maxTop}
            onSelect={onSelectTable}
          />
        ))}
        {tables.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
            No tables in this section
          </div>
        )}
      </div>
    </div>
  );
}
